<template>
    <v-form autocomplete="off" @submit.prevent="submit">
        <v-row v-if="title" dense>
            <v-col>
                <h4>{{ title }}</h4>
            </v-col>
        </v-row>
        <v-row dense>
            <v-col>
                <LabelField :label="$t('totp_code')">
                    <v-otp-input
                        ref="codeField"
                        v-model="code"
                        class="totp-code-input"
                        :length="codeLength"
                        :disabled="loading"
                        type="number"
                        @input="onInput"
                        @finish="submit"
                        @paste="onPaste"
                    />
                </LabelField>
                <p class="hint">{{ $t('totp_code_hint') }}</p>
            </v-col>
        </v-row>
        <v-row dense>
            <v-col class="text-right">
                <ActionBtn :loading="loading" :disabled="loading || !isComplete" type="submit" @click.prevent="submit">
                    <KpIcon icon-cls="arrow-bigright" color="white" />
                    <span>{{ $t('totp_verify') }}</span>
                </ActionBtn>
            </v-col>
        </v-row>
        <v-row dense>
            <v-col>
                <p>
                    <a href="#" @click.prevent="$emit('cancel')">{{ $t('totp_back_to_login') }}</a>
                </p>
            </v-col>
        </v-row>
        <v-row dense>
            <v-col>
                <v-fade-transition>
                    <v-alert v-if="error" type="error">{{ error }}</v-alert>
                </v-fade-transition>
            </v-col>
        </v-row>
    </v-form>
</template>

<script>
const CODE_LENGTH = 6;

/**
 * Entry of the 6 digit TOTP code. Used for both login steps: the onboarding (below the
 * QR code) and the normal code entry.
 *
 * Emits 'verify' with the cleaned code, and 'cancel' to go back to the login form.
 */
export default {
    name: 'TotpCode',
    props: {
        title: {
            type: String,
            default: '',
        },
        loading: {
            type: Boolean,
            default: false,
        },
        error: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            code: '',
            codeLength: CODE_LENGTH,
        };
    },
    computed: {
        isComplete() {
            return this.code.length === CODE_LENGTH;
        },
    },
    watch: {
        /**
         * While the parent fetches the setup data the field is disabled, and a disabled
         * input cannot take the focus. Focus it as soon as it becomes usable.
         */
        loading(isLoading, wasLoading) {
            if (wasLoading && !isLoading) {
                this.focusField();
            }
        },
    },
    mounted() {
        if (!this.loading) {
            this.focusField();
        }
    },
    methods: {
        /**
         * The boxes are text inputs, so keep only digits in the model - a letter would
         * otherwise block the verification with no visible reason.
         */
        onInput(value) {
            const digits = (value || '').replace(/\s/g, '');
            if (digits !== value) {
                this.code = digits;
            }
        },
        /**
         * Authenticator apps and password managers paste the code with a space in it
         * ("123 456"). The component itself would put the space into a box, so take the
         * digits out of the clipboard content and fill the boxes here. A complete code is
         * sent right away - a separate click adds nothing here.
         */
        onPaste(event) {
            const clipboard = event.clipboardData || window.clipboardData;
            if (!clipboard) {
                return;
            }
            const digits = (clipboard.getData('text') || '').replace(/\D/g, '').substring(0, CODE_LENGTH);
            if (!digits) {
                return;
            }
            event.preventDefault();
            this.code = digits;
            this.$nextTick(() => this.submit());
        },
        submit() {
            if (!this.isComplete || this.loading) {
                return;
            }
            this.$emit('verify', this.code);
        },
        /**
         * Called by the parent after a wrong code, so the user can type the next one.
         */
        reset() {
            this.code = '';
            this.focusField();
        },

        focusField() {
            this.$nextTick(() => this.$refs.codeField && this.$refs.codeField.focus());
        },
    },
};
</script>

<style lang="scss" scoped>
.hint {
    line-height: 1.4;
}

.totp-code-input {
    // the boxes carry their own outline - no extra gap below the label:
    max-width: 320px;
    padding: 0;
}
</style>
